/**
 * ovidTheme — Ovid's instance of `HouseTheme`: the house's words, picture paths and policies for the
 * shared wall, signed in or not.
 *
 * `MuseStrip` is loaded with `React.lazy`: the home route imports this theme for a visitor too, and
 * a static import would ship the muse strip, and the registry behind it, in the visitor's chunk.
 */
import {lazy, Suspense} from 'react'
import {RATIO_KEYS} from '@core/wall/contract'
import type {HouseTheme} from '@core/wall/ui/theme'
import {returnTokenFor} from '@core/wall/ui/chains'
import {OVID_ANON_CODA_ROWS} from './anonWall'
import {OVID_FACETS} from './facets'
import {OVID_ALLOWANCE_RETURN, ovidAllowanceBar} from './allowanceBar'

const MuseStrip = lazy(() => import('./MuseStrip').then((m) => ({default: m.MuseStrip})))

// The house's one generate word (plan `2026_09_18_ovid_wall_polish.md` § P5, decision 2): the
// verb button's own `verbs.generate` AND a visitor's open-cell door/label now read the same word,
// so the wall names the same action the same way whether the reader can press it or not. One
// constant rather than the object literal referencing its own `verbs.generate` mid-construction.
const RENDER = 'Render'

export const ovidTheme: HouseTheme = {
    id: 'ovid',
    facets: OVID_FACETS,
    // Narcis's value (operator, 2026-09-25): six columns of 2:3 at 1440, the two portrait houses alike.
    targetRowHeight: 339,
    gap: 8,
    // The column's own bar width (plan `2026_09_18_armed_frame_budget.md` § D5): narrower than
    // Toile's, since Ovid draws portrait — principle 2, "portrait houses take a narrower bar".
    armed: {barWidth: 520},
    // No ratio picker (plan § step 6's literal list) — Ovid renders its own house ratio; the field
    // stays hidden the way Narcis's own theme is expected to (BRIEF's "Toile-only" axis).
    hasRatioPicker: false,
    ratioKeys: RATIO_KEYS,
    defaultRatio: '2:3',
    // The pencil door (plan `2026_09_18_ovid_wall_polish.md` § P3, BRIEF §245-249): at Ovid the
    // field hides behind the pencil, unlike Toile where it is always on. `Column.tsx`/`Sheet.tsx`
    // read this to decide the whole bar's layout.
    pencil: true,
    verbs: {
        // "Render" is the house's own verb everywhere else a generate action is named
        // (`ovid/packs/copy.ts`'s `renderAsLabel`/`anonCtaLabel`) — reused here rather than
        // Toile's "Generate" so the wall reads as the same product as the pack pages.
        generate: RENDER,
        again: 'Again',
        placeholder: {onPicture: 'What should change?', onBlank: 'What do you want to see?'},
        // No ghost words of its own: the same sentences at every house (operator ruling,
        // 2026-09-18). The earlier single line, "Start with her." with no rule, rested on the
        // visitor wall carrying its own doors — but a visitor sees `visitor.ghostDoor`, never the
        // ghost, so the signed-in member was never told the rule.
        blankBasisLabel: 'blank canvas',
        refused: 'not something we draw',
    },
    // The words Ovid shipped; Toile and Narcis moved to `DEFAULT_CHROME` (operator, 2026-09-25).
    chrome: {
        back: {lead: '↓ Wall', desktop: 'scroll · or click the wall', phone: '· swipe down'},
        search: 'Search the wall',
        pencil: 'Direction',
        pin: {off: 'pin', on: 'unpin', idle: 'A picture you make can be pinned'},
        intake: 'A muse of your own',
    },
    // The picture allowance, said in the bar's own row (`allowanceBar.tsx`); the plan's checkout returns to the wall.
    allowanceBar: ovidAllowanceBar,
    allowanceReturn: OVID_ALLOWANCE_RETURN,
    // Same-origin, always — matching `products/toile/wall/theme.ts`'s own builders exactly (review
    // item C, adversarial pass on wall verbs, 2026-09-17: no client-readable base constant here; see
    // `ovid/config.ts`'s comment on the fallback resource routes that stand in for a CDN on dev).
    // The ingest's `image_key` already carries the full `pack-samples/<slug>/<file>` prefix, so
    // nothing is prepended. Deliberately NOT `OVID_PACK_SAMPLES_BASE_URL`/`OVID_GENERATIONS_BASE_URL`
    // — those are the OLD host's (ovidmuse.com, narcis.ai) cross-origin bases for the existing
    // marketing/pack surfaces, a separate concern from the wall's own.
    tileImageUrl: (imageKey: string) => `/${imageKey}`,
    takeImageUrl: (imageKey: string) => `/generations/${imageKey}`,
    previewImageUrl: (rowId: string, n: number) => `/previews/${rowId}/${n}.webp`,
    // Ovid's surface cut 404s everything but the wall itself — a `/<packSlug>`
    // link is dead, so the label renders as plain text instead (wall-ending pass, 2026-09-17).
    tileLabelHref: () => null,
    // Keep-only (plan `2026_10_25_wall_share.md` § 13): the picture leaves as the owner's own file —
    // no link, no take page, no words riding the system sheet.
    share: {
        link: false,
        fileBase: 'ovid',
        words: {
            door: 'Download or copy',
            native: 'Share…',
            download: 'Download',
            copyImage: 'Copy picture',
            copyLink: '',
            title: '',
            text: null,
        },
    },
    // The choose-state slot (plan § Decided 5) — `MuseStrip` reads `props` verbatim, nothing of the
    // theme's own state threaded through here. `fallback={null}`: the panel renders nothing while
    // its chunk loads rather than a placeholder shimmer — the column/sheet already have their own
    // layout without it, and the load is a one-time, sub-second fetch the first time a signed-in
    // wall renders the identity panel at all.
    identityPanel: (props) => (
        <Suspense fallback={null}>
            <MuseStrip {...props} />
        </Suspense>
    ),
    // The house is closed to a visitor (plan `2026_09_17_ovid_one_wall.md` § Decided 2-5): the same
    // `<Wall>`, fed the finite `AnonWallResponse`, with these answers standing in for what a
    // member's wall does. Every string below is one the veiled wall already shipped — the board's
    // two door words — moved onto the shared components rather than re-authored.
    visitor: {
        // An open cell's accessible name and hover door both read the house's own verb now (plan
        // § P5, decision 2: "the open tile's hover pill reads 'Render'") — a visitor cannot press
        // it (the tap opens the locked column, never `/continue` directly), but the word names
        // what pressing it will lead to, the same word the column's own verb button carries.
        anonLabel: {
            open: RENDER,
            veiled: 'Members only — sign in to see',
        },
        anonDoor: {
            // An open cell's door carries the pill alone (operator ruling, 2026-09-17: the shade by
            // itself read as unfinished, and a visitor's tile has no estate words to add).
            open: {line: null, cta: RENDER},
            veiled: {line: 'Members only', cta: 'Sign in to see'},
        },
        codaRows: OVID_ANON_CODA_ROWS,
        // The exact shape a member's own return token takes (`chains.returnTokenFor`): a tapped
        // tile re-arms on `/?basis=<tileId>` and the ghost's/header's door — no tile named — returns
        // to `/`. Built by reusing `returnTokenFor` itself against a one-off `Armed` value, so the
        // two can never drift apart; `Wall.tsx`'s own return-token handling (~line 1018) is what
        // reads this back on the far side of the round-trip.
        returnTo: (tileId, render = false) =>
            returnTokenFor(tileId !== null ? {kind: 'tile', tileId} : {kind: 'ghost'}, '', null, render),
    },
}
